import { Controller } from "@hotwired/stimulus";
import { computed, signal } from "@preact/signals-core";
import { type CombatBridge, combatBridge } from "../maps/combat_bridge";
import type { LiveToken } from "../types/generated/live";
import type MapBoard from "./map_board_controller";
import { closeOtherPanels } from "./map_panels";
// What takes the panel's own Escape first: the island's popups, a menu's trigger while the menu is open, and a modal dialog (the stat block's treasure table).
const NESTED = ".cs-popup, [aria-haspopup][aria-expanded='true'], dialog[open]";

// Connects to data-controller="map-combat", beside map-board on the master's root: the Combate panel, whose body is the
// `map_combat` island, mounted once the board is ready (docs/architecture/maps.md, "The Combate panel"). The island reaches the board
// through `combatBridge` only.
export default class extends Controller {
	static targets = ["panel", "toggle", "body", "host"];
	declare readonly panelTarget: HTMLElement;
	declare readonly toggleTarget: HTMLElement;
	declare readonly hasToggleTarget: boolean;
	declare readonly bodyTarget: HTMLElement;
	declare readonly hostTarget: HTMLTemplateElement;
	readonly shown = signal(false);

	connect() {
		// A root Turbo carries back is already booted, so no `ready` comes.
		if (this.board?.renderer) this.ready();
	}

	disconnect() {
		this.close();
		if (combatBridge.value) combatBridge.value = null;
	}

	get board() {
		return this.application.getControllerForElementAndIdentifier(this.element, "map-board") as MapBoard | null;
	}

	ready() {
		const board = this.board;
		if (!board?.state) return;
		const { state } = board;
		const sheetFor = signal<string | null>(null);
		const bridge: CombatBridge = {
			tokens: computed(() => (state.view.value.tokens ?? {}) as Record<string, LiveToken>),
			selection: computed(() => state.local.selection.value as string[]),
			rejected: computed(() => board.sync?.status.value === "rejected"),
			open: this.shown,
			ticks: state.local.ticks,
			marks: state.local.combat,
			sheetFor,
			openSheet: (id) => {
				sheetFor.value = id;
				if (!this.shown.value) this.show();
			},
			startTicks: (eligible) => board.startTicks(eligible),
			select: (id) => board.select(id),
			reveal: (id) => board.updateToken(id, { hidden: false }),
			remove: (id) => board.removeToken(id),
			place: (subject) => board.placeHidden(subject),
			close: () => this.close(),
			toast: (message) => board.toast(message),
		};
		combatBridge.value = bridge;
		// Mounted with the board rather than on the first open: the island's encounter client draws the overlays with the panel shut.
		this.mount();
	}

	mount() {
		if (!this.bodyTarget.firstElementChild) this.bodyTarget.append(this.hostTarget.content.cloneNode(true));
	}

	toggle() {
		if (this.shown.value) this.close();
		else this.show();
	}

	show() {
		closeOtherPanels(this.application, this.element, "map-combat");
		this.mount();
		this.panelTarget.hidden = false;
		this.shown.value = true;
		if (this.hasToggleTarget) this.toggleTarget.setAttribute("aria-pressed", "true");
		this.panelTarget.focus({ preventScroll: true });
	}

	// A popup or popover opened inside the panel takes the Escape first; so does an input that drops what was typed.
	escape(event: KeyboardEvent) {
		if (event.defaultPrevented || this.panelTarget.querySelector(NESTED)) return;
		// Stopped here, or the board's own Escape would also deselect behind the panel.
		event.preventDefault();
		event.stopPropagation();
		this.close();
	}

	close() {
		if (!this.shown.value) return;
		// Read before hiding: a hidden element gives its focus up at once.
		const focused = this.panelTarget.contains(document.activeElement);
		this.panelTarget.hidden = true;
		this.shown.value = false;
		// A request the island never took must not open a sheet the next time the panel shows.
		if (combatBridge.value) combatBridge.value.sheetFor.value = null;
		if (this.hasToggleTarget) this.toggleTarget.setAttribute("aria-pressed", "false");
		if (focused) (this.hasToggleTarget ? this.toggleTarget : this.board?.canvasTarget)?.focus({ preventScroll: true });
	}
}
